import { IButtonStyles, isIOS, PrimaryButton, Text } from "@fluentui/react";
import { IStackStyles, Stack, IStackTokens } from "@fluentui/react/lib/Stack";
import { getPerformance } from "@microsoft/1ds-core-js";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import analytics from "../../Initialisation";
import { IAttributes, ICustomEvent, IUCISModel } from "../../Interfaces/interface";
import useMediaQuery from "../../Utilities/useHooks";
import { uSDXGlobalObject } from "./../../GlobalVariables";
import { Link } from "react-router-dom";
import ProgressIndicator from "../ProgressIndicator/ProgressIndicator";
import { logUCMetrics } from "./UCMetricsLogger";
import { reportRawEvent } from "./RawEventLogger";
import { debounce } from "../../Utilities/debounce";
import { TABLET, DESKTOP, MOBILE, SMALL_HEIGHT_FOR_TABLET_AND_DESKTOP, HIGH_CONSTRANST_MODE_ENABLED, SMALL_HEIGHT} from "../../Utilities/mediaTypes";
import { isAndroid } from "../../Utilities/common";

function getCustomProperties(stTime: number) {

    let perf = getPerformance();
    let windowPerformanceTiming = perf?.timing;

    //timing=object of PerformanceTiming
    if (windowPerformanceTiming) {
        let event: ICustomEvent = {};

        //calculate Click Time. This will be equal to the difference in currentTime(time of button click and start time
        let NavigationStart = windowPerformanceTiming.navigationStart;
        if (NavigationStart && NavigationStart !== 0) {
            event.TimeToClick = new Date().getTime() - stTime;
        }

        //Calculate Page Load Time
        // page Load Time is calculated by difference in navigation start and loadEventStart(time just before the load event is fired whicih means page was rendered)
        if (windowPerformanceTiming.loadEventStart && windowPerformanceTiming.navigationStart) {

            //event.ViewLoadTime = windowPerformanceTiming.loadEventStart - windowPerformanceTiming.navigationStart;
            event.ViewLoadTime = stTime - windowPerformanceTiming.navigationStart;
        }

        // returns Telemetry context object which holds values related to location, app, user etc.
        let Properties = analytics.getPropertyManager().getPropertiesContext();

        //get location (returns timezone)
        event.Location = Properties.loc.tz;
        return event;
    }
    return -1;
}

function onAcknowledgement(stTime: number, ucisData: IUCISModel, setLoading: React.Dispatch<React.SetStateAction<boolean>>) {
    let dic = {
        baseType: "buttonClickData",
        content: {
            baseData: {},
        },
        name: "buttonClickTimeAfterPageLoad"
    };

    let event = getCustomProperties(stTime);

    // puts {key,value} pair in "content" of dictionary of values to be passed to custom event
    Object.keys(event).forEach((key) => {
        dic.content[key] = event[key];
    });

    reportRawEvent(dic);

    logUCMetrics("ConsentAccepted", ucisData);

    let formData = new FormData();
    formData.append("ClientId", ucisData.ClientId);
    formData.append("ConsentSurface", ucisData.ConsentSurface);
    formData.append("ConsentType", ucisData.ConsentType);
    formData.append("correlation_id", ucisData.CorrelationId);
    formData.append("CountryRegion", ucisData.CountryRegion);
    formData.append("DeviceId", ucisData.DeviceId);
    formData.append("SerializedEncryptionData", ucisData.SerializedEncryptionData);
    formData.append("FormFactor", ucisData.FormFactor);
    formData.append("Market", ucisData.Market);
    formData.append("ModelType", ucisData.ModelType);
    formData.append("ModelVersion", ucisData.ModelVersion);
    formData.append("NoticeId", ucisData.NoticeId);
    formData.append("Platform", ucisData.Platform);
    formData.append("UserId", ucisData.UserId);
    formData.append("UserVersion", ucisData.UserVersion.toString());

    axios.post(
        "/recordnotice",
        formData,
        {
            timeout: 12000,
        }
    ).then(() => {
        window.location.replace(encodeURI(ucisData.RedirectUrl));
    }).catch(() => {
        window.location.replace(encodeURI(ucisData.RedirectUrl));
    });

}

// tslint:disable-next-line: max-func-body-length
export const InterruptPage: React.FC = () => {

    const ucisData: IUCISModel = window.ucis;
    const ucisModelTexts: IAttributes = ucisData?.ModelSchema?.modelTexts[0].attributes;

    const [loading, setLoading] = useState<boolean>(false);
    const debounceDelayInMS = 100;

    const Acknowledgement = () => {
        const container = document.getElementById("scrollDiv") as HTMLDivElement;
        if (!learnMoreViewed && container != null) {
            container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
        } else {
            setLoading(true);
            setEnableButton(false);
            onAcknowledgement(uSDXGlobalObject.stTime, ucisData, setLoading);
        }
    };

    let isTablet = useMediaQuery(TABLET);
    let isMobile = useMediaQuery(MOBILE);
    let isDesktop = useMediaQuery(DESKTOP);
    let isSmallHeightForTabletAndDesktop = useMediaQuery(SMALL_HEIGHT_FOR_TABLET_AND_DESKTOP);
    let isMobileIOs = isIOS() && isMobile;
    let isMobileAndroid = isAndroid() && isMobile;
    let isNonIOSNonAndroidMobileView = !isMobileIOs && !isMobileAndroid && isMobile;
    let isHighContrastModeEnabled = useMediaQuery(HIGH_CONSTRANST_MODE_ENABLED);
    let isSmallHeight = useMediaQuery(SMALL_HEIGHT);

    let eventListenerCalled = useRef(false);
    const getScrollRef = React.useRef<HTMLDivElement>(null);
    const learnMoreRef = React.useRef<HTMLSpanElement>(null);
    const [enableButton, setEnableButton] = useState<boolean>(false);
    const [learnMoreViewed, setLearnMoreViewed] = useState<boolean>(false);
    const [isBoxShadowEnabled, setBoxShadowEnabled] = useState<boolean>(false);


    // images
    const [productLogoSource, setProductLogoSource] = useState<string>(ucisData.ProductLogoSource);
    const [microsoftLogoSource, setMicrosoftLogoSource] = useState<string>(ucisData.MicrosoftLogoSrc);
    const [microsoftHighContrastLogoSrc, setMicrosoftHighContrastLogoSrc] = useState<string>(ucisData.MicrosoftHighContrastLogoSrc);

    useEffect(() => {
        if (eventListenerCalled.current === true) {
            return;
        }
        eventListenerCalled.current = true;

        function setStTime() {
            if (uSDXGlobalObject.stTime === 0) {
                uSDXGlobalObject.updateTime(Date.now());
            }
        }
        setStTime();

        logUCMetrics("ConsentRenderSuccess", ucisData);
    });

    useEffect(() => {
        if (enableButton) {
            logUCMetrics("ConsentContinueButtonEnabled", ucisData);
        }
    }, [enableButton]);

    const getDialogBodyStyles = (): IStackStyles => {
        return {
            root: {
                backgroundColor: "#ffffff",
                bottom: (isMobileAndroid || isNonIOSNonAndroidMobileView) ? "0" : "auto",
                boxShadow: "0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2);",
                height: (isDesktop && !isSmallHeightForTabletAndDesktop) ? "724px" : "100%",
                minHeight: (isMobile || isTablet || isSmallHeightForTabletAndDesktop) ? "100%" : "auto",
                minWidth: (isMobile || isTablet || isSmallHeight) ? "100%" : "auto",
                position: (isMobileAndroid) ? "fixed" : "relative",
                width: isMobile ? "100%" : "440px"
            }
        };
    };

    const getEmailTextStyles = (): IStackStyles => {
        return {
            root: {
                color: isMobile ? "#242424" : "#1B1B1B",
                fontSize: isMobile ? "14px" : "15px",
                fontStyle: isMobile ? "Roboto" : "Segoe UI",
                fontWeight: "400",
            }
        };
    };

    const getEmailWrapperStyles = (): IStackStyles => {
        return {
            root: {
                lineHeight: isMobile ? "16px" : "20px",
                wordBreak: "break-all"
            }
        };
    };

    const getHeaderTextStyles = (): IStackStyles => {
        return {
            root: {
                color: isMobile ? "#242424" : "#1B1A19",
                fontSize: "24px",
                fontStyle: isMobile ? "Roboto" : "Segoe UI",
                fontWeight: "600",
                lineHeight: "32px",
            }
        };
    };

    const getSubHeaderTextStyles = (): IStackStyles => ({
        root: {
            color: "#242424",
            fontSize: "14px",
            fontStyle: isMobile ? "Roboto" : "Segoe UI",
            fontWeight: "700",
            lineHeight: "20px"
        }
    });

    const getSubHeaderBodyTextStyles = (): IStackStyles => {
        return {
            root: {
                color: "#242424",
                fontSize: "14px",
                fontStyle: isMobile ? "Roboto" : "Segoe UI",
                fontWeight: "400",
                lineHeight: "20px"
            }
        };
    };

    const stackStyles: IStackStyles = {
        root: {
            height: isTablet ? "581px" : "auto",
            marginLeft: (isTablet || isSmallHeightForTabletAndDesktop) ? "auto" : (isMobile ? "24px" : "44px"),
            marginRight: (isTablet || isSmallHeightForTabletAndDesktop) ? "auto" : (isMobile ? "24px" : "0px"),
            marginTop: isMobile ? "24px" : "44px",
            position: "relative",
            width: (isTablet || isDesktop) ? "352px" : "auto"
        },
    };

    const productIconStackStyle: IStackStyles = {
        root: {
            height: isDesktop ? "128px" : "auto"
        },
    };

    const parentStackStyles: IStackStyles = {
        root: {
            marginTop : "16px"
        },
    };

    const getFooterStyles = (): IStackStyles => {
        return {
            root: {
                height: "auto",
                paddingTop: (isSmallHeightForTabletAndDesktop) ? "8px" : "0px",
                width: "auto"
            }
        };
    };

    const buttonStyles: IButtonStyles = {
        root: {
            backgroundColor: "#0067B8",
            borderRadius: isMobile ? "4px" : "0px",
            fontSize: isMobile ? "14px" : "15px",
            fontWeight: "400",
            height: "32px",
            lineHeight: isMobile ? "16px" : "20px",
            margin: (isTablet || isSmallHeight) ? "6px calc((100vw - 352px) / 2) 46px 46px " :  (isMobile ? "6px 24px 46px 0px" : "0px 44px 44px 0px"),
            width: "120px"
        },
        rootFocused: {
            textDecoration: "underline"
        }
    };

    const getLinkStyles = (): IStackStyles => {
        return {
            root: {
                lineHeight: "20px"
            }
        };
    };

    const getLinkTextStyle: React.CSSProperties = {
        color: "#0067B8",
        fontSize: "14px",
        fontStyle: isMobile ? "Roboto" : "Segoe UI",
        fontWeight: 400,
        lineHeight: isMobile ? "16px" : "20px",
        textDecoration: "underline"
    };

    const verticalGapStackTokens: IStackTokens = {
        childrenGap: 24,
    };

    const verticalGapTextTokens: IStackTokens = {
        childrenGap: 4,
    };

    const verticalGapStacks: IStackTokens = {
        childrenGap: 12,
    };

    const scrollStyles = (): IStackStyles => {
        return {
            root: {
                boxShadow: isBoxShadowEnabled ? "rgba(0, 0, 0, 0.24) 0px -8px 6px -6px inset" : "none",
                height: "auto",
                marginBottom: "0px",
                maxHeight: "calc(100vh - 84px)",
                overflowY: "auto",
                paddingBottom: "22px"
            }
        };
    };

    useEffect(() => {
        const container = document.getElementById("scrollDiv") as HTMLDivElement;

        const onScroll = () => {
            const bottomLen = Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight);

            //if bottomLen <= 2 px then disable the shadow
            if (bottomLen <= 2) {
                setBoxShadowEnabled(false);
            } else {
                setBoxShadowEnabled(true);
            }
        };

        //runs at start to detect box-shadow state
        onScroll();
        setEnableButton(true);

        const onScrollDebounce = debounce(onScroll, debounceDelayInMS);
        const onResizeDebounce = debounce(() => {
            onScroll();
        }, debounceDelayInMS);

        container?.addEventListener("scroll", onScrollDebounce, { passive: true });
        container?.addEventListener("touchmove", onScrollDebounce, { passive: true });
        window?.addEventListener("resize", onResizeDebounce, { passive: true });

        return () => {
            container?.removeEventListener("scroll", onScrollDebounce);
            container?.removeEventListener("touchmove", onScrollDebounce);
            window.removeEventListener("resize", onResizeDebounce);
        };

    }, [getScrollRef.current]);

    useEffect(() => {
        const learnMorecallbackFunction: IntersectionObserverCallback = (entries: IntersectionObserverEntry[]) => {
            const [entry] = entries;
            if (entry.intersectionRatio === 1) {
                setLearnMoreViewed(true);
            }
        };

        const observer = new IntersectionObserver(learnMorecallbackFunction, {
            threshold: 1.0
        } as IntersectionObserverInit);

        if (learnMoreRef.current) {
            observer.observe(learnMoreRef.current);
        }

        return () => {
            if (learnMoreRef.current) { observer.unobserve(learnMoreRef.current); }
        };
    }, [learnMoreRef]);

    const onProductImageLoadError = () => {
        setProductLogoSource("/Images/newProductLogoLightMode-512x512.png");
    };

    const onMicrosoftLogoLoadError = () => {
        if (isHighContrastModeEnabled) {
            setMicrosoftHighContrastLogoSrc("/Images/microsoftLogoHighContrast.svg");
        } else {
            setMicrosoftLogoSource("/Images/microsoftLogoLightMode.svg");
        }
    };

    const getProductLogoStyle: React.CSSProperties = {
        alignSelf: "center",
        height: "128px",
        width: "128px"
    };

    const getLogoStyle: React.CSSProperties = {
        height: "24px",
        width: "112.55px"
    };

    //tslint:disable : use-simple-attributes
    return (
        <Stack styles={getDialogBodyStyles} className="interrupt-content" >
            {loading ? (<ProgressIndicator />) : ""}
            <Stack styles={scrollStyles} id="scrollDiv">
                <Stack.Item styles={stackStyles} id="interruptContainer">
                    <div ref={getScrollRef}></div>
                    <Stack>
                        <img
                            src={isHighContrastModeEnabled ? microsoftHighContrastLogoSrc : microsoftLogoSource}
                            alt="Microsoft"
                            style={getLogoStyle}
                            onError={onMicrosoftLogoLoadError}
                        />
                    </Stack>
                    <Stack styles={parentStackStyles} tokens={verticalGapStacks}>
                        <Stack.Item align="start" styles={getEmailWrapperStyles}>
                            <Text variant="mediumPlus" styles={getEmailTextStyles}>{ucisData?.EmailId}</Text>
                        </Stack.Item>

                        <Stack.Item align="start">
                            <Text role="heading" aria-level={1} variant="xxLarge" styles={getHeaderTextStyles}>{ucisModelTexts?.title}</Text>
                        </Stack.Item>

                        <Stack styles={productIconStackStyle}>
                            <img
                                src={productLogoSource}
                                alt={ucisModelTexts?.["image-altText"]}
                                style={getProductLogoStyle}
                                onError={onProductImageLoadError}
                            />
                        </Stack>

                        <Stack horizontalAlign="start" tokens={verticalGapStackTokens}>
                            <Stack tokens={verticalGapTextTokens}>
                                <Text role="heading" aria-level={2} variant="medium" styles={getSubHeaderTextStyles} >
                                {ucisModelTexts?.["section1-header"]}
                                </Text>
                                <Text variant="medium" styles={getSubHeaderBodyTextStyles} >
                                {ucisModelTexts?.["section1-description"]}
                                </Text>
                            </Stack>

                            <Stack tokens={verticalGapTextTokens}>
                                <Text role="heading" aria-level={2} variant="medium" styles={getSubHeaderTextStyles} >
                                {ucisModelTexts?.["section2-header"]}
                                </Text>
                                <Text variant="medium" styles={getSubHeaderBodyTextStyles} >
                                {ucisModelTexts?.["section2-description"]}
                                </Text>
                            </Stack>

                            <Stack tokens={verticalGapTextTokens}>
                                <Text role="heading" aria-level={2} variant="medium" styles={getSubHeaderTextStyles} >
                                {ucisModelTexts?.["section3-header"]}
                                </Text>
                                <Text variant="medium" styles={getSubHeaderBodyTextStyles} >
                                {ucisModelTexts?.["section3-description"]}
                                </Text>
                            </Stack>
                        </Stack>

                        <Stack.Item styles={getLinkStyles}>
                            <span ref={learnMoreRef}>
                                <Link to="/LearnMore" style={getLinkTextStyle} aria-label={ucisModelTexts?.["learnMore-aria-label"]}>
                                    {ucisModelTexts?.["learnMore-linkText"] ? ucisModelTexts?.["learnMore-linkText"] : "Learn more"}
                                </Link>
                            </span>
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
            </Stack>
            <Stack id="StickyFooter" styles={getFooterStyles} horizontalAlign="end">
                <PrimaryButton onClick={Acknowledgement} text={ucisModelTexts?.["continue-buttonText"]} styles={buttonStyles} disabled={!enableButton} />
            </Stack>
        </Stack>
    );
};
